import { useEffect, useState } from "react";
import { Auth0UserSerializer, BetaFeature } from "recoil/types";
import { fetchOrLogout } from "../../../utils/helpers";
import { getBaseUrl } from "../../../utils/requestUtils";
import { TrackAnalyticsEvent } from "../../../services/Analytics";

// Hook used to fetch all beta features on page load
export const useBetaFeatures = (
  token: string | undefined,
  setLoggedOut: (inactivity: boolean) => void,
): BetaFeature[] => {
  const [betaFeatures, setBetaFeatures] = useState<BetaFeature[]>([]);
  useEffect(() => {
    const fetchBetaFeatures = async () => {
      if (!token) {
        return;
      }
      try {
        const baseUrl = await getBaseUrl();
        const result = await fetchOrLogout(
          `${baseUrl}/api/v1/provider_portal/beta_features/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
          setLoggedOut,
        );

        if (result.ok) {
          setBetaFeatures(await result.json());
        }
      } catch (e) {
        return [];
      }
    };
    fetchBetaFeatures();
  }, [token]);
  return betaFeatures;
};

// Call the beta_feature API to check if the user has responded to a beta feature
// This method does not care what the response is, it only matters whether the user has responded at all
export const getShouldShowBetaBanner = async (
  token: string,
  featureName: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<boolean> => {
  try {
    const baseUrl = await getBaseUrl();
    const result = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/beta_feature_responses/?feature__name=${featureName}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );

    if (result.ok) {
      const resultJson = await result.json();
      // resultJson's length will be 0 if the user has never responded to the feature
      return resultJson.length === 0;
    }
    return false;
  } catch (e) {
    return false;
  }
};

// Call the beta_feature API to record user response to a beta feature
export const RespondToFeatureBanner = async (
  featureId: number,
  isOptedIn: boolean,
  setLoggedOut: (inactivity: boolean) => void,
  token?: string,
): Promise<boolean> => {
  if (!token) {
    return false;
  }
  try {
    const baseUrl = await getBaseUrl();
    const result = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/beta_feature_responses/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          feature: featureId,
          is_opted_in: isOptedIn,
        }),
      },
      setLoggedOut,
    );

    if (result.ok) {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

// Handler function to be passed to the banner buttons
export const handleBetaFeatureResponse = async (
  featureId: number,
  featureName: string,
  isOptedIn: boolean,
  auth: Auth0UserSerializer,
  setLoggedOut: (inactivity: boolean) => void,
  setShouldShowBetaBanner: (show: boolean) => void,
) => {
  // Don't wait for success to hide the bannner. If something goes wrong,
  // we'll just show it on the next page load.
  setShouldShowBetaBanner(false);
  // Call the API to record the response
  const success = await RespondToFeatureBanner(
    featureId,
    isOptedIn,
    setLoggedOut,
    auth?.token,
  );
  // If the call succeeded, send the response to mixpanel
  if (success) {
    TrackAnalyticsEvent("BetaFeatureResponse", auth?.token, undefined, {
      feature_name: featureName,
      is_opted_in: isOptedIn ? "yes" : "no",
    });
  }
};
